// app/shared/providers/ReactQueryProvider.tsx
// v2

// app/shared/providers/ReactQueryProvider.tsx
"use client";

import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';

import { useToast } from '@/components/ui/use-toast';

// React Query DevTools are permanently disabled to prevent HMR errors
// DO NOT re-enable them - they cause module factory issues with Next.js Turbopack

export function ReactQueryProvider({ children }: React.PropsWithChildren) {
  const { toast } = useToast();
  
  // Setup global error handling for external JSON parsing errors
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (event.reason?.message?.includes('not valid JSON')) {
        // console.warn('[ReactQueryProvider] Caught external JSON parsing error:', event.reason);
        event.preventDefault(); // Prevent the error from crashing the app
      }
    };

    const handleError = (event: ErrorEvent) => {
      if (event.message?.includes('not valid JSON')) {
        // console.warn('[ReactQueryProvider] Caught external JSON parsing error:', event.error);
        event.preventDefault();
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, []);
  
  const [queryClient] = useState(() => new QueryClient({
    queryCache: new QueryCache({
      onError: (error: any) => {
        // Don't show toasts for JSON parsing errors from external sources
        if (error?.message?.includes('not valid JSON')) {
          // console.warn('React Query - External JSON parsing error ignored:', error);
          return;
        }
        
        console.error('React Query - Global Query Error:', error);
        toast({
          variant: "destructive",
          title: "Data Fetching Error",
          description: error?.message || "An error occurred while fetching data. Please try again.",
        });
      },
    }),
    mutationCache: new MutationCache({
      onError: (error: any) => {
        // Don't show toasts for JSON parsing errors from external sources
        if (error?.message?.includes('not valid JSON')) {
          // console.warn('React Query - External JSON parsing error ignored:', error);
          return;
        }
        
        console.error('React Query - Global Mutation Error:', error);
        toast({
          variant: "destructive",
          title: "Action Failed",
          description: error?.message || "An error occurred while performing an action. Please try again.",
        });
      },
    }),
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: (failureCount, error: any) => {
          // Don't retry JSON parsing errors
          if (error?.message?.includes('not valid JSON')) {
            return false;
          }
          return failureCount < 1;
        },
      },
    }
  }));

  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  );
}

